/**
 * **每一 t 时刻标量场的初始状态** —— 从 `old_repo` 里那套 alexharri 渐变背景
 * 移植过来的 GLSL。
 *
 * 评审：「在所有的扰动之前，将 `old_repo/src/shaders/alexharri` 的计算结果
 * 作为每一 t 值时标量场的初始状态。」
 *
 * ## 它算什么
 *
 * 原版是一张"会流动的渐变背景"：一层底噪 + **两条**被噪声推着上下起伏的光带
 * （wave），用 `wave_alpha` 把三层明度混起来。原来最后一步把它映射到一张
 * 渐变贴图（`u_gradient`）上出颜色 —— 我们不取那一步：
 *
 * ```
 *   alexharri：明度 ──▶ 渐变贴图 ──▶ RGB
 *   我们：     明度 ──▶ 标量场 ──▶ 非线性映射 ──▶ 半调（纸上只有黑白两色）
 * ```
 *
 * 所以这里只保留**明度**那一半（0..1）。多了颜色反而和"酸印刷"打架：
 * 背景的颜色只有纸色和墨色两个来源（见 `palette.ts`）。
 *
 * ## 移植时改了什么（都很小，但都要知道）
 *
 * | 原版 | 这里 | 为什么 |
 * | --- | --- | --- |
 * | `gl_FragCoord`（屏幕像素、y 朝上） | `logical`（画布逻辑坐标、y 朝下） | 我们的场铺满整块视口，坐标必须是画布逻辑坐标才不随窗口漂 |
 * | `get_x() = 900 + gl_FragCoord.x - u_w/2` | `900 + logical.x` | 900 那个偏移是"别从噪声原点开始"；我们锚在**画布**上（和网点同一套纪律） |
 * | `0.45 * u_h` / `0.9 * u_h` | `0.45 * uResolution.y` | 画布高度是常量 800 |
 * | `smoothstep(float)`（重载） | `ah_smoothstep5()` | 重载内置函数在 GLSL ES 里是雷区，改名最省事 |
 * | `u_time` | `uBaseSpeed * uTime` | 场自己的时间轴（现在慢十倍）之外再给一个倍率，调参页要 |
 * | 三个 `L`（空间频率） | 乘 `uBaseScale` | 同上 |
 *
 * ## 许可
 *
 * 下面两段 GLSL 分别来自 **Alex Harri** 的渐变背景
 * （<https://alexharri.com/blog/webgl-gradients>，MIT）和它内嵌的
 * **Ashima Arts / stegu 的 webgl-noise**（MIT）。两份许可都要求保留版权声明，
 * 所以原文抄在这里：
 *
 * ```
 * Copyright (c) 2025 Alex Harri Jónsson
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
 * of the Software, and to permit persons to whom the Software is furnished to do
 * so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * ---
 *
 * simplex noise: Copyright (C) 2011 Ashima Arts. All rights reserved.
 * Distributed under the MIT License.
 * https://github.com/ashima/webgl-noise / https://github.com/stegu/webgl-noise
 * ```
 */

/** 光带模糊的采样数。原版默认 7：越大边越软，也越贵（每像素多 7 次 pow/smoothstep） */
export const AH_BLUR_QUALITY = 7;

/** 原版的可调参数（现在是我们的常量，改这里就够） */
export const AH_DEFAULTS = {
  blurAmount: 345,
  blurExponentMin: 0.9,
  blurExponentMax: 1.2,
} as const;

/**
 * 两段工具函数 + 整个 alexharri 的明度计算。
 *
 * 对外只暴露 `ah_base(vec2 logical, float time)`：给定画布逻辑坐标和时间，
 * 返回 0..1 的明度 —— 那就是"标量场在这一时刻的初始状态"。
 */
export const ALEXHARRI_GLSL = /* glsl */ `
// ---------------------------------------------------------------- 工具 ----
// 引用自 Ashima Arts / stegu 的 webgl-noise（MIT，见本文件的头部说明）
vec2 ah_mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 ah_mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 ah_mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 ah_permute(vec4 x) { return ah_mod289(((x * 34.0) + 1.0) * x); }
vec3 ah_permute(vec3 x) { return ah_mod289(((x * 34.0) + 10.0) * x); }
vec4 ah_taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float ah_simplex3(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = ah_mod289(i);
  vec4 p = ah_permute(ah_permute(ah_permute(
             i.z + vec4(0.0, i1.z, i2.z, 1.0))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0))
           + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = ah_taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

float ah_simplex2(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);

  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;

  i = ah_mod289(i);
  vec3 p = ah_permute(ah_permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));

  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;

  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;

  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);

  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

// ------------------------------------------------------- alexharri 那一套 ----
// 五次的 smoothstep。原版**重载**了内置的 smoothstep —— GLSL ES 里那是有雷的，
// 所以改名，调用点也显式用这个名字
float ah_smoothstep5(float t) {
  return t * t * t * (t * (6.0 * t - 15.0) + 10.0);
}

float ah_lerp(float a, float b, float t) { return a * (1.0 - t) + b * t; }
float ah_ease_in(float x) { return 1.0 - cos((x * 3.14159) * 0.5); }

/**
 * 底噪：三层 simplex 叠出来的明度（原版 background_noise）。
 *
 * ⚠️ logical.x 上加 900 是**原版的偏移**（get_x()）—— 从噪声原点开始的话，
 * 整片背景会正好落在 simplex 的格点上，能看出方格。
 */
float ah_background_noise(vec2 logical, float time, float offset) {
  const float S = 0.064;
  const float L = 0.00085;
  const float L1 = 1.5, L2 = 0.9, L3 = 0.6;
  const float LY1 = 1.00, LY2 = 0.85, LY3 = 0.70;
  const float F = 0.04;
  const float Y_SCALE = 1.0 / 0.27;

  float x = (900.0 + logical.x) * L * uBaseScale;
  float y = logical.y * L * Y_SCALE * uBaseScale;
  float t = time + offset;
  float x_shift = t * F;
  float sum = 0.5;
  sum += ah_simplex3(vec3(x * L1 + x_shift * 1.1, y * L1 * LY1, t * S)) * 0.30;
  sum += ah_simplex3(vec3(x * L2 + -x_shift * 0.6, y * L2 * LY2, t * S)) * 0.25;
  sum += ah_simplex3(vec3(x * L3 + x_shift * 0.8, y * L3 * LY3, t * S)) * 0.20;
  return sum;
}

/**
 * 光带边缘的高低起伏（原版 wave_y_noise）。它才是"波带在动"的来源。
 *
 * ⚠️ x 必须**逐像素**传进来（原版是 get_x()，跟着 gl_FragCoord.x 走）——
 * 写成常量的话光带的边就成了一条直线，"波浪"整个没了
 */
float ah_wave_y_noise(float x, float time, float offset) {
  const float S = 0.075;
  const float F = 0.026;

  float t = time + offset;
  float y = t * S;
  float x_shift = t * F;

  float sum = 0.0;
  sum += ah_simplex2(vec2(x * 1.30 + x_shift, y * 0.54)) * 0.85;
  sum += ah_simplex2(vec2(x * 1.00 + x_shift, y * 0.68)) * 1.15;
  sum += ah_simplex2(vec2(x * 0.70 + x_shift, y * 0.59)) * 0.60;
  sum += ah_simplex2(vec2(x * 0.40 + x_shift, y * 0.48)) * 0.40;
  return sum;
}

/** 模糊量随时间的慢摆动（原版 calc_blur_bias + calc_blur） */
float ah_blur_bias(float time) {
  const float S = 0.261;
  float bias_t = (sin(time * S) + 1.0) * 0.5;
  return ah_lerp(-0.17, -0.04, bias_t);
}

float ah_calc_blur(float x, float time, float offset) {
  const float S = 0.07;
  const float F = 0.03;

  float t = time + offset;
  float blur_fac = ah_blur_bias(time);
  blur_fac += ah_simplex2(vec2(x * 0.60 + t * F * 1.0, t * S * 0.7)) * 0.5;
  blur_fac += ah_simplex2(vec2(x * 1.30 + t * F * -0.8, t * S * 1.0)) * 0.4;
  blur_fac = (blur_fac + 1.0) * 0.5;
  return clamp(blur_fac, 0.0, 1.0);
}

/** 一条光带的软边。7 次采样把不同模糊量的边叠起来（原版 wave_alpha_part 的积分） */
float ah_wave_alpha_part(float dist, float blur_fac, float t) {
  float e = mix(${AH_DEFAULTS.blurExponentMin.toFixed(5)}, ${AH_DEFAULTS.blurExponentMax.toFixed(5)}, t);
  float v = pow(blur_fac, e);
  v = ah_ease_in(v);
  v = ah_smoothstep5(v);
  v = clamp(v, 0.008, 1.0);
  v *= ${AH_DEFAULTS.blurAmount.toFixed(1)};
  float alpha = clamp(0.5 + dist / v, 0.0, 1.0);
  return ah_smoothstep5(alpha);
}

float ah_wave_alpha(vec2 logical, float waveY, float waveHeight, float time, float offset) {
  // 两个随 x 变化的量（原版从 get_x() 来），频率在调用点算好传进来
  const float LX = 0.000845;
  const float LB = 0.0011;
  float nx = (900.0 + logical.x) * LX * uBaseScale;
  float bx = (900.0 + logical.x) * LB * uBaseScale;

  float wave_y = waveY + ah_wave_y_noise(nx, time, offset) * waveHeight;
  float dist = wave_y - logical.y;
  float blur_fac = ah_calc_blur(bx, time, offset);

  const float PART = 1.0 / float(${AH_BLUR_QUALITY});
  float sum = 0.0;
  for (int i = 0; i < ${AH_BLUR_QUALITY}; i++) {
    sum += ah_wave_alpha_part(dist, blur_fac, PART * float(i)) * PART;
  }
  return sum;
}

/**
 * **标量场在这一时刻的初始状态**（0..1 的明度）。
 *
 * 三层明度按两条光带的 alpha 混起来 —— 和原版 main() 的最后三行一模一样，
 * 只是把"查渐变贴图上色"换成了"直接交出明度"。
 */
float ah_base(vec2 logical, float time) {
  float bg = ah_background_noise(logical, time, -192.4);
  float w1 = ah_background_noise(logical, time, 273.3);
  float w2 = ah_background_noise(logical, time, 623.1);

  float w1_alpha = ah_wave_alpha(logical, 0.45 * uResolution.y, 0.195 * uResolution.y, time, 112.5 * 48.75);
  float w2_alpha = ah_wave_alpha(logical, 0.90 * uResolution.y, 0.144 * uResolution.y, time, 225.0 * 36.00);

  float lightness = bg;
  lightness = ah_lerp(lightness, w2, w2_alpha);
  lightness = ah_lerp(lightness, w1, w1_alpha);
  return lightness;
}
`;

